import { baseApi } from '@/shared/api/baseApi';
import type {
  CreateOffsetRequest,
  CreatePaymentRequest,
  ListPaymentsParams,
  PaymentDto,
  PaymentListPage,
  PaymentPreviewDiff,
  PreviewPaymentRequest,
  RemovePaymentRequest,
  SetPaymentAllocationsRequest,
  UpdatePaymentRequest,
} from '../model/types';

const paymentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listPayments: builder.query<PaymentListPage, ListPaymentsParams>({
      query: (params) => ({ url: 'payments', params: toQueryParams(params) }),
      providesTags: ['PaymentList'],
    }),
    getPayment: builder.query<PaymentDto, string>({
      query: (id) => `payments/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Payment', id }],
    }),
    // A payment's FIFO allocation can touch documents this call never names (§7), so every
    // mutation here invalidates the bare 'Document' tag (no id) rather than trying to enumerate
    // affected ids — RTK Query matches that against every `{type:'Document', id}` a
    // `useGetDocumentQuery` currently provides, refreshing any open DocumentWindow's paidAmount.
    createPayment: builder.mutation<PaymentDto, CreatePaymentRequest>({
      query: (body) => ({ url: 'payments', method: 'POST', body }),
      invalidatesTags: ['PaymentList', 'Counterparty', 'Document', 'DocumentList', 'DocumentSummary'],
    }),
    createOffset: builder.mutation<PaymentDto, CreateOffsetRequest>({
      query: (body) => ({ url: 'payments/offset', method: 'POST', body }),
      invalidatesTags: ['PaymentList', 'Counterparty', 'Document', 'DocumentList', 'DocumentSummary'],
    }),
    updatePayment: builder.mutation<PaymentDto, { id: string; body: UpdatePaymentRequest }>({
      query: ({ id, body }) => ({ url: `payments/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Payment', id }, 'PaymentList', 'Counterparty', 'Document', 'DocumentList', 'DocumentSummary'],
    }),
    setPaymentAllocations: builder.mutation<PaymentDto, { id: string; body: SetPaymentAllocationsRequest }>({
      query: ({ id, body }) => ({ url: `payments/${id}/allocations`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Payment', id }, 'PaymentList', 'Counterparty', 'Document', 'DocumentList', 'DocumentSummary'],
    }),
    deletePayment: builder.mutation<void, { id: string; body: RemovePaymentRequest }>({
      query: ({ id, body }) => ({ url: `payments/${id}`, method: 'DELETE', body }),
      invalidatesTags: ['PaymentList', 'Counterparty', 'Document', 'DocumentList', 'DocumentSummary'],
    }),
    // §8.5: считает последствия update/setAllocations/remove, ничего не сохраняет — не инвалидирует теги.
    previewPayment: builder.mutation<PaymentPreviewDiff, { id: string; body: PreviewPaymentRequest }>({
      query: ({ id, body }) => ({ url: `payments/${id}/preview`, method: 'POST', body }),
    }),
  }),
});

function toQueryParams(params: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    out[key] = String(value);
  }
  return out;
}

export const {
  useListPaymentsQuery,
  useLazyListPaymentsQuery,
  useGetPaymentQuery,
  useCreatePaymentMutation,
  useCreateOffsetMutation,
  useUpdatePaymentMutation,
  useSetPaymentAllocationsMutation,
  useDeletePaymentMutation,
  usePreviewPaymentMutation,
} = paymentApi;

export { paymentApi };
